/**
 * Default Fuel Prices — Q1 2026
 *
 * Prices are GROSS (including VAT) per native fuel unit.
 * Updated quarterly based on URE tariffs and market data.
 *
 * Sources:
 * - URE (Urząd Regulacji Energetyki) — gas & electricity tariffs
 * - e-petrol.pl — heating oil & LPG prices
 * - muratordom.pl — coal, pellet, wood prices (Feb 2026)
 * - POBE kalkulator — district heating tariffs
 */

import type { FuelPrice, FuelVariantId } from '../types/heatingCost.types';

/** Timestamp of last price update */
export const PRICES_LAST_UPDATED = '2026-01-15';
export const PRICES_SOURCE = 'URE, e-petrol.pl, muratordom.pl';

/**
 * Default gross prices per fuel unit.
 * Gas prices: total cost per kWh (energy + distribution + abonament).
 * Based on Tauron Gliwice tariffs from the reference image.
 */
export const DEFAULT_FUEL_PRICES: Record<FuelVariantId, FuelPrice> = {
  // Gas: ~0.49-0.50 zł/kWh (total cost including distribution, URE 2025/2026)
  gas_old_boiler: {
    variantId: 'gas_old_boiler',
    pricePerUnit: 0.50,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  gas_traditional_boiler: {
    variantId: 'gas_traditional_boiler',
    pricePerUnit: 0.49,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  gas_condensing_boiler: {
    variantId: 'gas_condensing_boiler',
    pricePerUnit: 0.49,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  // LPG: 2.20 zł/litr (propan)
  lpg_condensing_boiler: {
    variantId: 'lpg_condensing_boiler',
    pricePerUnit: 2.20,
    priceUnit: 'zł/litr',
    isUserModified: false,
  },
  // Heating oil: 5.29 zł/litr
  oil_traditional_boiler: {
    variantId: 'oil_traditional_boiler',
    pricePerUnit: 5.29,
    priceUnit: 'zł/litr',
    isUserModified: false,
  },
  oil_condensing_boiler: {
    variantId: 'oil_condensing_boiler',
    pricePerUnit: 5.29,
    priceUnit: 'zł/litr',
    isUserModified: false,
  },
  // Coal: 1600-1800 zł/tona (market rates 2025/2026)
  coal_stoker: {
    variantId: 'coal_stoker',
    pricePerUnit: 1600,
    priceUnit: 'zł/tona',
    isUserModified: false,
  },
  coal_ecopea: {
    variantId: 'coal_ecopea',
    pricePerUnit: 1800,
    priceUnit: 'zł/tona',
    isUserModified: false,
  },
  // Wood: 1000 zł/m.p. (buk), 1800 zł/tona (pelet, market rate 2025/2026)
  wood_log_boiler: {
    variantId: 'wood_log_boiler',
    pricePerUnit: 1000,
    priceUnit: 'zł/m.p.',
    isUserModified: false,
  },
  wood_pellet_boiler: {
    variantId: 'wood_pellet_boiler',
    pricePerUnit: 1800,
    priceUnit: 'zł/tona',
    isUserModified: false,
  },
  // Electricity G11: 0.9839 zł/kWh (total with distribution)
  heat_pump_ground: {
    variantId: 'heat_pump_ground',
    pricePerUnit: 0.9839,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  heat_pump_air: {
    variantId: 'heat_pump_air',
    pricePerUnit: 0.9839,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  // Storage heaters: G12 night rate 0.5597 zł/kWh
  storage_heater: {
    variantId: 'storage_heater',
    pricePerUnit: 0.5597,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  // Electric boiler: G11 0.9839 zł/kWh
  electric_boiler: {
    variantId: 'electric_boiler',
    pricePerUnit: 0.9839,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
  // District heating: ~0.30 zł/kWh (typical Polish tariff)
  district_heating: {
    variantId: 'district_heating',
    pricePerUnit: 0.30,
    priceUnit: 'zł/kWh',
    isUserModified: false,
  },
};

/** Get a copy of default prices (to avoid mutation) */
export function getDefaultFuelPrices(): FuelPrice[] {
  return Object.values(DEFAULT_FUEL_PRICES).map((p) => ({ ...p }));
}
